import { breakpointSizes, getBreakpointDown } from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import CsvInputBox from 'src/components/CsvInputBox/CsvInputBox'
import { CsvInputFieldTemplateOption } from 'src/components/CsvInputField/CsvInputField'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { fileIsEmpty } from 'src/utils/validation/file.validation'
import { css } from 'styled-components'

interface BulkUploadFormProps {
  testId?: string
  onSuccessfulSubmit: (flatfields: BulkUploadFormFlatFields) => void
  postingBulkUpload: boolean
  uploadError?: string
  title: React.ReactNode
  subtitle: React.ReactNode
  buttonText: React.ReactNode
  buttonTextWithRowCount: string
  note?: React.ReactElement
  templateDownloadOptions: Array<CsvInputFieldTemplateOption<string>>
  loadingTemplate: boolean
}

export type BulkUploadFormFlatFields = {
  csv?: File
}

// Use this form for synchronous imports (ex: Expense Category Labels)
const BulkUploadForm: React.FC<BulkUploadFormProps> = (
  props,
): React.ReactNode => {
  const translate = useTranslate()

  const {
    fields: flatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<BulkUploadFormFlatFields>(
    {},
    (): FormFieldsValidation<BulkUploadFormFlatFields> => {
      return {
        csv: [fileIsEmpty],
      }
    },
    props.uploadError !== undefined
      ? {
          csv: [props.uploadError],
        }
      : undefined,
    undefined,
    {
      csv: 0,
    },
  )

  const [rowCount, setRowCount] = useState<number | undefined>()
  useEffect(() => {
    if (flatFields.csv !== undefined) {
      const reader = new FileReader()
      reader.onload = (event: ProgressEvent<FileReader>): void => {
        // event.target?.result format depends on how it was triggered - using readAsText means this returns string
        const csvData = event.target?.result as string
        // number of rows to upload = number of lines in csv minus header
        const rows = csvData?.split('\n').length - 1
        setRowCount(rows)
      }
      reader.readAsText(flatFields.csv)
    } else {
      setRowCount(undefined)
    }
  }, [flatFields.csv])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    const flatFieldsPassedValidation = flatFieldsSetErrors()
    if (flatFieldsPassedValidation) {
      props.onSuccessfulSubmit(flatFields)
    }
  }

  return (
    <form onSubmit={onSubmit}>
      <CsvInputBox
        id={props.testId || 'csvInputBox'}
        title={props.title}
        subtitle={props.subtitle}
        note={props.note}
        csvProps={{
          value: flatFields.csv,
          errors: flatFieldsErrors.csv,
          onChange: (val: File): void => {
            onFlatFieldChange('csv', val)
          },
        }}
        templateDownloadOptions={props.templateDownloadOptions}
        loadingTemplate={props.loadingTemplate}
      />
      <Button
        testId={'uploadBtn'}
        css={css`
          margin-top: 40px;
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            margin-top: 30px;
            width: 100%;
          `)}
        `}
        design={ButtonDesigns.designCta}
        type={ButtonTypes.submit}
        big={true}
        loadingText={translate('Uploading...')}
        text={
          rowCount && rowCount > 0
            ? translate(props.buttonTextWithRowCount, {
                rowCount: rowCount.toString(),
              })
            : props.buttonText
        }
        loading={props.postingBulkUpload}
        disabled={props.postingBulkUpload}
      />
    </form>
  )
}

export default BulkUploadForm
